import type { TTurnstileErrorValue, TTurnstileSuccessValue } from '../../types/data';
import type { TTurnstileOption } from '../../types/options';

export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const TURNSTILE_LOAD_TIMEOUT_MS = 15000;
const TURNSTILE_FLOW = 'turnstile';

export const TurnstileClientError = {
    LOAD_FAILED: 'load_failed',
    NO_NETWORK: 'no_network',
    // Part of the cross-platform contract (native WebView). Not emitted on the web: the widget is embedded directly in the page.
    WEBVIEW_UNAVAILABLE: 'webview_unavailable',
} as const;

export const TurnstileLoadError = {
    SCRIPT_LOAD_ERROR: 'script_load_error',
    SCRIPT_LOAD_TIMEOUT: 'script_load_timeout',
    API_MISSING: 'api_missing',
    UNSUPPORTED_BROWSER: 'unsupported_browser',
} as const;

export type TTurnstileAppearance = NonNullable<TTurnstileOption['appearance']>;
export type TTurnstileSize = NonNullable<TTurnstileOption['size']>;

export type TTurnstileRenderOptions = {
    sitekey: string;
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    size?: TTurnstileSize;
    appearance?: TTurnstileAppearance;
    retry?: 'auto' | 'never';
    'refresh-expired'?: 'auto' | 'manual' | 'never';
    callback?: (token: string) => void;
    'error-callback'?: (errorCode: string) => boolean | void;
    'expired-callback'?: (token: string) => void;
    'unsupported-callback'?: () => void;
    'before-interactive-callback'?: () => void;
    'after-interactive-callback'?: () => void;
};

export type TTurnstileApi = {
    render: (container: HTMLElement | string, options: TTurnstileRenderOptions) => string | null | undefined;
    remove: (widgetId: string) => void;
    reset: (widgetId?: string) => void;
    ready?: (callback: () => void) => void;
};

declare global {
    interface Window {
        turnstile?: TTurnstileApi;
    }
}

export const normalizeAppearance = (value?: string): TTurnstileAppearance => (value === 'interaction-only' ? 'interaction-only' : 'always');

export const normalizeSize = (value?: string): TTurnstileSize => (value === 'normal' || value === 'compact' ? value : 'flexible');

export const buildSuccessValue = (token: string): TTurnstileSuccessValue => ({ status: 'success', flow: TURNSTILE_FLOW, token });

export const buildErrorValue = (errorCode: string, errorMessage?: string): TTurnstileErrorValue =>
    errorMessage ? { status: 'error', flow: TURNSTILE_FLOW, errorCode, errorMessage } : { status: 'error', flow: TURNSTILE_FLOW, errorCode };

let loaderPromise: Promise<TTurnstileApi> | undefined;

/** Loads the Cloudflare Turnstile script once and shares it between all Turnstile widgets on the page. */
export const loadTurnstileScript = (timeoutMs: number = TURNSTILE_LOAD_TIMEOUT_MS): Promise<TTurnstileApi> => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (loaderPromise) {
        return loaderPromise;
    }

    loaderPromise = new Promise<TTurnstileApi>((resolve, reject) => {
        const script = document.createElement('script');

        const fail = (reason: string) => {
            clearTimeout(timer);
            script.onload = null;
            script.onerror = null;
            script.remove();
            loaderPromise = undefined;
            reject(new Error(reason));
        };

        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            clearTimeout(timer);
            if (window.turnstile) {
                resolve(window.turnstile);
            } else {
                fail(TurnstileLoadError.API_MISSING);
            }
        };
        script.onerror = () => fail(TurnstileLoadError.SCRIPT_LOAD_ERROR);
        // Handlers above only fire asynchronously (after appendChild), so `timer` is always initialised by then.
        const timer = setTimeout(() => fail(TurnstileLoadError.SCRIPT_LOAD_TIMEOUT), timeoutMs);

        document.head.appendChild(script);
    });

    return loaderPromise;
};

export const __resetTurnstileLoaderForTests = () => {
    loaderPromise = undefined;
};
